/**
 * The Game layer, backend and video-app view: every saved diagram resolves to exactly one game.
 *
 * A *game* is the sport-and-variant a coach says they play (Flag 5v5, Girls' lacrosse). It is not
 * a film game (the `games` table). This file mirrors `shared/core/games.ts` in
 * `@playside/diagram-core` (the editor's view) for the facts the backend and the video app need:
 * ids, sport, labels, counts, surfaces, nav, palette-row key, and the rule facts that live here
 * only (build.md D5). The backend runtime does not import diagram-core, hence the mirror.
 * `diagrams/src/lib/games-drift.test.ts` fails when the two disagree.
 * Design: docs/exec-plans/active/multi-sport-experience/build.md §2.1, §2.3, §2.4.
 */
import { FIELD_TYPES, type SavedFieldType } from "./team-country";

export const GAME_IDS = [
  "football-11",
  "football-ca-12",
  "football-8",
  "flag-5",
  "flag-7",
  "basketball",
  "lacrosse-boys",
  "lacrosse-girls",
  "cheer",
  "hockey",
  // Legacy games: one per sport the engine already draws, so every saved diagram resolves to a
  // game. Always off for creation; never shown on tiles.
  "soccer",
  "rugby",
  "volleyball",
  "baseball",
  "field-hockey",
  "water-polo",
] as const;
export type GameId = (typeof GAME_IDS)[number];

export function isGameId(value: unknown): value is GameId {
  return typeof value === "string" && (GAME_IDS as readonly string[]).includes(value);
}

/** The games the product does not offer; they exist so every saved diagram resolves to a game. */
export const LEGACY_GAME_IDS = [
  "soccer",
  "rugby",
  "volleyball",
  "baseball",
  "field-hockey",
  "water-polo",
] as const;
export type LegacyGameId = (typeof LEGACY_GAME_IDS)[number];

export function isLegacyGame(game: GameId): game is LegacyGameId {
  return (LEGACY_GAME_IDS as readonly string[]).includes(game);
}

export type DiagramSport =
  | "football"
  | "basketball"
  | "hockey"
  | "lacrosse"
  | "cheer"
  | "soccer"
  | "rugby"
  | "volleyball"
  | "baseball"
  | "field-hockey"
  | "water-polo";

/** Players a side puts on the surface in a situation. */
export interface SideCount {
  /** Players excluding the goalie. */
  field: number;
  /** 1 when the side has a goalie in this situation; absent = 0. */
  goalie?: number;
}
/** One-sided games (cheer): how many athletes a routine can have. */
export interface RosterRange {
  min: number;
  max: number;
}
export interface GameNav {
  callSheets: boolean;
  wristbands: boolean;
  film: boolean;
}

export type GameRuleFacts =
  | {
      kind: "football";
      /** 4; 3 for Canadian. */
      downs: number;
      /** "ten-yards": a 10-yd line to gain. "zone-lines": the next painted zone line (flag). */
      lineToGain: "ten-yards" | "zone-lines";
      /** false for flag. */
      kickoffs: boolean;
      /** Rushers must start this far from the LOS; null = no rule. */
      rushLineYards: number | null;
      /** Every player is an eligible receiver (flag). */
      allEligible: boolean;
    }
  | { kind: "basketball"; periods: number; shotClockSeconds: number | null }
  | { kind: "lacrosse"; shotClockSeconds: number | null; possessionClockSeconds: number | null }
  | { kind: "cheer"; maxRoutineSeconds: number; beatsPerEight: 8 }
  | { kind: "none" };

export interface GameFacts {
  id: GameId;
  sport: DiagramSport;
  label: string;
  tileLabel: string;
  sides: "two" | "one";
  /** The default situation's counts (the first one in the editor's registry). */
  defaultCounts: { offense: SideCount | RosterRange; defense: SideCount | null };
  formationCap: number;
  /** Football formations must have exactly formationCap players; other games at most. */
  formationRule: "exact" | "at-most";
  /** Picker order; [0] is the default. Football's are field types, lacrosse's variants (D2). */
  surfaces: readonly string[];
  paletteRows: "sport" | "game";
  nav: GameNav;
  rules: GameRuleFacts;
}

const players = (field: number, goalie?: number): SideCount => (goalie ? { field, goalie } : { field });
const NAV_ON: GameNav = { callSheets: true, wristbands: true, film: true };
const NAV_OFF: GameNav = { callSheets: false, wristbands: false, film: false };
const NO_RULES: GameRuleFacts = { kind: "none" };
const LACROSSE_RULES: GameRuleFacts = {
  kind: "lacrosse",
  shotClockSeconds: null, // NFHS optional 70-s clock is a state choice
  possessionClockSeconds: null,
};

const footballRules = (
  rules: Omit<Extract<GameRuleFacts, { kind: "football" }>, "kind">
): GameRuleFacts => ({ kind: "football", ...rules });

const legacyGame = (id: LegacyGameId, label: string, offense: SideCount | RosterRange, defense: SideCount): GameFacts => ({
  id,
  sport: id,
  label,
  tileLabel: label,
  sides: "two",
  defaultCounts: { offense, defense },
  formationCap: 11,
  formationRule: "at-most",
  surfaces: [],
  paletteRows: "sport",
  nav: NAV_OFF,
  rules: NO_RULES,
});

export const GAMES: Record<GameId, GameFacts> = {
  "football-11": {
    id: "football-11",
    sport: "football",
    label: "Football 11",
    tileLabel: "11-man",
    sides: "two",
    defaultCounts: { offense: players(11), defense: players(11) },
    formationCap: 11,
    formationRule: "exact",
    surfaces: ["highschool", "ncaa", "nfl"],
    paletteRows: "sport",
    nav: NAV_ON,
    rules: footballRules({
      downs: 4,
      lineToGain: "ten-yards",
      kickoffs: true,
      rushLineYards: null,
      allEligible: false,
    }),
  },
  "football-ca-12": {
    id: "football-ca-12",
    sport: "football",
    label: "Canadian 12",
    tileLabel: "Canadian",
    sides: "two",
    defaultCounts: { offense: players(12), defense: players(12) },
    formationCap: 12,
    formationRule: "exact",
    surfaces: ["canadian", "cfl"],
    paletteRows: "sport",
    nav: NAV_ON,
    rules: footballRules({
      downs: 3,
      lineToGain: "ten-yards",
      kickoffs: true,
      rushLineYards: null,
      allEligible: false,
    }),
  },
  "football-8": {
    id: "football-8",
    sport: "football",
    label: "8-man",
    tileLabel: "8-man",
    sides: "two",
    defaultCounts: { offense: players(8), defense: players(8) },
    formationCap: 8,
    formationRule: "exact",
    surfaces: ["eight-80x40", "eight-100x40"],
    paletteRows: "game",
    nav: NAV_ON,
    rules: footballRules({
      downs: 4,
      lineToGain: "ten-yards",
      kickoffs: true,
      rushLineYards: null,
      allEligible: false,
    }),
  },
  "flag-5": {
    id: "flag-5",
    sport: "football",
    label: "Flag 5v5",
    tileLabel: "Flag 5v5",
    sides: "two",
    defaultCounts: { offense: players(5), defense: players(5) },
    formationCap: 5,
    formationRule: "exact",
    surfaces: ["flag5-70x30"],
    paletteRows: "game",
    nav: NAV_ON,
    // NFL FLAG: 4 downs to cross midfield, 4 more to score; rush line 7 yd from the LOS.
    rules: footballRules({
      downs: 4,
      lineToGain: "zone-lines",
      kickoffs: false,
      rushLineYards: 7,
      allEligible: true,
    }),
  },
  "flag-7": {
    id: "flag-7",
    sport: "football",
    label: "Flag 7v7",
    tileLabel: "Flag 7v7",
    sides: "two",
    defaultCounts: { offense: players(7), defense: players(7) },
    formationCap: 7,
    formationRule: "exact",
    surfaces: ["flag7-80x40", "flag7-full"],
    paletteRows: "game",
    nav: NAV_ON,
    // NFHS girls' flag. kickoffs and rushLineYards are UNVERIFIED: confirm with the pilot coach.
    rules: footballRules({
      downs: 4,
      lineToGain: "zone-lines",
      kickoffs: false,
      rushLineYards: null,
      allEligible: true,
    }),
  },
  basketball: {
    id: "basketball",
    sport: "basketball",
    label: "Basketball",
    tileLabel: "Basketball",
    sides: "two",
    defaultCounts: { offense: players(5), defense: players(5) },
    formationCap: 5,
    formationRule: "at-most",
    surfaces: ["court-nfhs-84", "court-nfhs-94", "court-ncaa", "court-nba", "court-fiba"],
    paletteRows: "sport",
    nav: NAV_OFF,
    // The shot clock is state-dependent in NFHS.
    rules: { kind: "basketball", periods: 4, shotClockSeconds: null },
  },
  "lacrosse-boys": {
    id: "lacrosse-boys",
    sport: "lacrosse",
    label: "Boys' lacrosse",
    tileLabel: "Boys'",
    sides: "two",
    defaultCounts: { offense: players(6), defense: players(6, 1) },
    formationCap: 10,
    formationRule: "at-most",
    surfaces: ["boys-hs", "boys-ncaa"],
    paletteRows: "sport",
    nav: NAV_OFF,
    rules: LACROSSE_RULES,
  },
  "lacrosse-girls": {
    id: "lacrosse-girls",
    sport: "lacrosse",
    label: "Girls' lacrosse",
    tileLabel: "Girls'",
    sides: "two",
    defaultCounts: { offense: players(7), defense: players(7, 1) },
    formationCap: 12,
    formationRule: "at-most",
    surfaces: ["girls-hs", "girls-ncaa"],
    paletteRows: "game",
    nav: NAV_OFF,
    rules: LACROSSE_RULES, // optional 90-s possession clock is a state choice
  },
  cheer: {
    id: "cheer",
    sport: "cheer",
    label: "Cheer",
    tileLabel: "Cheer",
    sides: "one",
    defaultCounts: { offense: { min: 5, max: 38 }, defense: null },
    formationCap: 38,
    formationRule: "at-most",
    surfaces: ["mat-9", "mat-7"],
    paletteRows: "sport",
    nav: NAV_OFF,
    rules: { kind: "cheer", maxRoutineSeconds: 150, beatsPerEight: 8 }, // UCA school 2:30
  },
  hockey: {
    id: "hockey",
    sport: "hockey",
    label: "Hockey",
    tileLabel: "Hockey",
    sides: "two",
    defaultCounts: { offense: players(5, 1), defense: players(5, 1) },
    formationCap: 6,
    formationRule: "at-most",
    surfaces: ["rink-nhl"],
    paletteRows: "sport",
    nav: NAV_OFF,
    rules: NO_RULES,
  },
  soccer: legacyGame("soccer", "Soccer", players(10, 1), players(10, 1)),
  rugby: legacyGame("rugby", "Rugby", players(15), players(15)),
  // Volleyball and baseball have no fixed roster on the batting/serving side, so the offense is a range.
  volleyball: legacyGame("volleyball", "Volleyball", { min: 1, max: 6 }, players(6)),
  baseball: legacyGame("baseball", "Baseball", { min: 1, max: 4 }, players(9)),
  "field-hockey": legacyGame("field-hockey", "Field hockey", players(10, 1), players(10, 1)),
  "water-polo": legacyGame("water-polo", "Water polo", players(6, 1), players(6, 1)),
};

/** The games of a sport, in tile order. Unknown sport: empty. */
export function gamesOfSport(sport: string): readonly GameId[] {
  return GAME_IDS.filter((id) => GAMES[id].sport === sport);
}

/**
 * Football surface facts the video app and crop tool need. Mirrors the editor's `FIELD_PRESETS`
 * (goal line to goal line, end zone depth and sideline to sideline, in yards); the drift test
 * fails if they differ.
 */
export const FOOTBALL_SURFACE_FACTS: Record<
  SavedFieldType,
  { goalToGoal: number; endZoneDepth: number; width: number }
> = {
  highschool: { goalToGoal: 100, endZoneDepth: 10, width: 53.33 },
  ncaa: { goalToGoal: 100, endZoneDepth: 10, width: 53.33 },
  nfl: { goalToGoal: 100, endZoneDepth: 10, width: 53.33 },
  canadian: { goalToGoal: 110, endZoneDepth: 20, width: 65 },
  cfl: { goalToGoal: 110, endZoneDepth: 20, width: 65 },
  "eight-80x40": { goalToGoal: 80, endZoneDepth: 10, width: 40 },
  "eight-100x40": { goalToGoal: 100, endZoneDepth: 10, width: 40 },
  "flag5-70x30": { goalToGoal: 50, endZoneDepth: 10, width: 30 },
  "flag7-80x40": { goalToGoal: 80, endZoneDepth: 10, width: 40 },
  "flag7-full": { goalToGoal: 100, endZoneDepth: 10, width: 53.33 },
};

const FIELD_TYPE_GAME: Record<SavedFieldType, GameId> = {
  highschool: "football-11",
  ncaa: "football-11",
  nfl: "football-11",
  canadian: "football-ca-12",
  cfl: "football-ca-12",
  "eight-80x40": "football-8",
  "eight-100x40": "football-8",
  "flag5-70x30": "flag-5",
  "flag7-80x40": "flag-7",
  "flag7-full": "flag-7",
};

/** Football only: the game a saved field type belongs to. An unknown or absent field is Football 11. */
export function gameOfFieldType(fieldType: unknown): GameId {
  return typeof fieldType === "string" && (FIELD_TYPES as readonly string[]).includes(fieldType)
    ? FIELD_TYPE_GAME[fieldType as SavedFieldType]
    : "football-11";
}

/**
 * The game a diagram is for, from the `diagrams.game` column and the parsed `diagram_data`.
 * Same rules as `gameOfDiagram` in diagram-core; never throws, junk reads as the sport's first
 * game. An explicit valid `game` whose sport matches the diagram's wins. Otherwise football
 * derives from its field type, lacrosse from its variant's gender (`girls-*` is
 * `lacrosse-girls`), every other sport is its single game. An absent sport is football.
 */
export function deriveGame(input: {
  sport?: string | null;
  game?: unknown;
  fieldType?: unknown;
  lacrosseVariant?: unknown;
}): GameId {
  const sport: string =
    typeof input.sport === "string" && gamesOfSport(input.sport).length > 0 ? input.sport : "football";
  if (isGameId(input.game) && GAMES[input.game].sport === sport) return input.game;
  if (sport === "football") return gameOfFieldType(input.fieldType);
  if (sport === "lacrosse") {
    return input.lacrosseVariant === "girls-hs" || input.lacrosseVariant === "girls-ncaa"
      ? "lacrosse-girls"
      : "lacrosse-boys";
  }
  return gamesOfSport(sport)[0];
}

/** A team's game: `teams.game`, else from `teams.country` (ca is Canadian 12, anything else Football 11). */
export function teamGameOf(team: { game?: string | null; country?: string | null }): GameId {
  if (isGameId(team.game)) return team.game;
  return team.country === "ca" ? "football-ca-12" : "football-11";
}

/**
 * The game a saved formation is for, from its `game` and `sport` columns and the `field_type` it
 * was drawn on. Both columns are NULL on rows saved before games: the game then derives from the
 * field (canadian and cfl are Canadian 12, the multi-sport fields their own games, anything else
 * Football 11) and the sport is football. Never throws.
 */
export function formationGameOf(row: {
  game?: unknown;
  sport?: unknown;
  field_type?: unknown;
}): GameId {
  return deriveGame({ sport: row.sport as string | null | undefined, game: row.game, fieldType: row.field_type });
}

/**
 * The game a served formation belongs to, for filtering lists by a play's game. The server derives
 * `game` for a row that never had one (`game_derived`), from its field alone; an older fieldless
 * side with more than 11 players can only be a 12-a-side lineup, so it is Canadian 12, the same
 * call `formationOpenGame` makes when the formation opens.
 */
export function formationListGame(row: {
  game?: unknown;
  sport?: unknown;
  field_type?: unknown;
  game_derived?: boolean;
  side?: string;
  symbols?: ReadonlyArray<{ layer: string }> | null;
}): GameId {
  if (row.game_derived && !row.field_type && row.side && row.symbols) {
    const players = row.symbols.filter((symbol) => symbol.layer === row.side).length;
    if (players > 11) return "football-ca-12";
  }
  return formationGameOf(row);
}

/**
 * The game a formation opens in an editor. A formation saved with a game (or, before games, a
 * field) keeps it. One never saved with either (blank placeholders, older rows) opens in its
 * team's game when its side is empty. A drawn one was drawn 11 a side and stays Football 11, even
 * after its team switches to Canadian 12 or Flag 5v5, unless it has more than 11 players: only a
 * 12-a-side lineup can, so it is Canadian 12 whatever its team plays now. A team whose game is
 * not a football one (the formation editor draws football only) reads as Football 11. `team` is
 * the owning team (null for a personal formation).
 */
export function formationOpenGame(
  saved: { game?: unknown; sport?: unknown; field_type?: unknown },
  playerCount: number,
  team: { game?: string | null; country?: string | null } | null | undefined
): GameId {
  if (isGameId(saved.game) || (typeof saved.field_type === "string" && (FIELD_TYPES as readonly string[]).includes(saved.field_type))) {
    return formationGameOf(saved);
  }
  const teamGame = team ? teamGameOf(team) : "football-11";
  if (GAMES[teamGame].sport !== "football") return "football-11";
  if (playerCount > 11) return "football-ca-12";
  return playerCount === 0 ? teamGame : "football-11";
}

/**
 * The field a formation opens on in `game`. A saved field that belongs to the game is kept (an
 * NFL lineup stays on the NFL field); otherwise the game's first surface (Football 11 high
 * school, Canadian 12 Canadian, Flag 5v5 its 70 x 30 field). The formation editors draw football
 * fields only until the editor follows the play's game (M6), so any other game opens on a high
 * school field.
 */
export function formationOpenSurface(saved: unknown, game: GameId): SavedFieldType {
  if (GAMES[game].sport !== "football") return "highschool";
  if (typeof saved === "string" && (FIELD_TYPES as readonly string[]).includes(saved) && gameOfFieldType(saved) === game) {
    return saved as SavedFieldType;
  }
  return GAMES[game].surfaces[0] as SavedFieldType;
}

/** Players a formation side must have (exact) or may have (at-most). */
export function formationPlayerLimit(game: GameId): { rule: "exact" | "at-most"; count: number } {
  return { rule: GAMES[game].formationRule, count: GAMES[game].formationCap };
}

const playerWord = (n: number) => (n === 1 ? "player" : "players");

/**
 * Why a formation can't be saved with `playerCount` players in `game`, or null when the count is
 * right. Football games need exactly the cap; every other game allows up to it.
 */
export function formationPlayerCountError(playerCount: number, game: GameId): string | null {
  const { rule, count } = formationPlayerLimit(game);
  if (rule === "exact") {
    if (playerCount === count) return null;
    const suffix = `Formations must have ${count} players.`;
    if (playerCount < count) {
      const missing = count - playerCount;
      return `Add ${missing} more ${playerWord(missing)} before saving. ${suffix}`;
    }
    const extra = playerCount - count;
    return `Remove ${extra} ${playerWord(extra)} before saving. ${suffix}`;
  }
  if (playerCount <= count) return null;
  const extra = playerCount - count;
  return `Remove ${extra} ${playerWord(extra)} before saving. Formations can have up to ${count} players.`;
}

// What a new play starts on, as the hint says it ("New plays start on a 70 × 30 flag field.").
const STARTS_ON: Record<GameId, string | null> = {
  "football-11": "a high school field",
  "football-ca-12": "a Canadian field",
  "football-8": "an 80 × 40 field",
  "flag-5": "a 70 × 30 flag field",
  "flag-7": "an 80 × 40 flag field",
  basketball: "a high school court",
  "lacrosse-boys": "a high school field",
  "lacrosse-girls": "a high school field",
  cheer: null, // worded separately: a routine, not a play
  hockey: "an NHL rink",
  soccer: null,
  rugby: null,
  volleyball: null,
  baseball: null,
  "field-hockey": null,
  "water-polo": null,
};

/**
 * The line under "What do you play?": "5 a side. New plays start on a 70 × 30 flag field."
 * `pickedFromLocation` appends " Picked from your location."; `existingPlays` appends
 * " Existing plays keep their field." (team settings, where plays already exist). Same options
 * as `teamCountryHint`, and the same words for Football 11 and Canadian 12. `pickedNote` appends
 * a sentence naming where a preselect came from ("Picked from your plays: 31 of your 40 are
 * Football 11."); `team-game-choice.ts` writes it.
 */
export function gameHint(
  game: GameId,
  options: { pickedFromLocation?: boolean; existingPlays?: boolean; pickedNote?: string | null } = {}
): string {
  const facts = GAMES[game];
  let hint: string;
  if (game === "cheer") {
    const roster = facts.defaultCounts.offense as RosterRange;
    hint = `Athletes ${roster.min}\u2013${roster.max}. New routines start on a 9-strip mat.`;
  } else {
    const startsOn = STARTS_ON[game];
    hint = `${facts.formationCap} a side.${startsOn ? ` New plays start on ${startsOn}.` : ""}`;
  }
  if (options.existingPlays) hint += " Existing plays keep their field.";
  if (options.pickedNote) hint += ` ${options.pickedNote}`;
  else if (options.pickedFromLocation) hint += " Picked from your location.";
  return hint;
}
